import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'

import type { CatalogItemDto } from '../api/catalogTypes'
import {
  listRestaurantMyPricesItems,
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/myPrices'
import { addToCart, setCartQuantity } from '../cart/cartStore'
import { useCart } from '../hooks/useCart'
import { formatUnitLabel } from '../utils/formatUnitLabel'
import { displaySupplierName, shortenSupplierName } from '../utils/supplierName'

type SearchScreenProps = {
  organizationId: number
}

const formatPrice = (price: number | null): string => {
  if (price == null) {
    return '—'
  }
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(price)
}

const formatDelta = (pct: number | null): string | null => {
  if (pct == null || !Number.isFinite(pct)) {
    return null
  }
  const rounded = Math.round(pct * 10) / 10
  if (rounded > 0) {
    return `▲ ${rounded}%`
  }
  if (rounded < 0) {
    return `▼ ${Math.abs(rounded)}%`
  }
  return null
}

const formatDaysAgo = (iso: string): string => {
  const observed = new Date(iso)
  if (Number.isNaN(observed.getTime())) {
    return '—'
  }
  const now = new Date()
  const diffMs = now.getTime() - observed.getTime()
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (days <= 0) {
    return 'сегодня'
  }
  if (days === 1) {
    return 'вчера'
  }
  return `${days} дн назад`
}

const formatUpdatedAt = (item: CatalogItemDto): string => {
  if (item.updatedAt) {
    return formatDaysAgo(item.updatedAt)
  }
  if (item.daysSinceUpdate != null && Number.isFinite(item.daysSinceUpdate)) {
    const days = Math.max(0, Math.floor(item.daysSinceUpdate))
    if (days <= 0) {
      return 'сегодня'
    }
    if (days === 1) {
      return 'вчера'
    }
    return `${days} дн назад`
  }
  return '—'
}

const rawItemName = (item: CatalogItemDto): string => {
  const positionName = item.positionName?.trim() ?? ''
  if (positionName) {
    return positionName
  }
  const supplierName = item.supplierName?.trim() ?? ''
  const country = item.country?.trim() ?? ''
  if (supplierName && supplierName.toLowerCase() !== country.toLowerCase()) {
    return supplierName
  }
  if (item.productTypeName?.trim()) {
    return item.productTypeName.trim()
  }
  if (supplierName) {
    return supplierName
  }
  if (item.externalSku?.trim()) {
    return item.externalSku.trim()
  }
  return `Позиция #${item.id}`
}

type SearchItemCardProps = {
  item: CatalogItemDto
  qty: number
  open: boolean
  onToggle: () => void
  onAdd: () => void
  onQty: (next: number) => void
}

const SearchItemCard = ({
  item,
  qty,
  open,
  onToggle,
  onAdd,
  onQty,
}: SearchItemCardProps) => {
  const delta = formatDelta(item.priceDeltaPct)
  const supplier =
    item.privateSupplierName?.trim() ||
    (item.privateSupplierId != null ? `Поставщик #${item.privateSupplierId}` : '—')

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }

  return (
    <li className={`rp-card rp-card--expandable${open ? ' rp-card--open' : ''}`}>
      <div className="rp-card__top">
        <button
          type="button"
          className="rp-card__toggle"
          aria-expanded={open}
          onClick={onToggle}
          onKeyDown={handleKeyDown}
        >
          <p className="rp-card__title">{rawItemName(item)}</p>
          <div className="rp-card__pricerow">
            <p className="rp-card__price">
              {formatPrice(item.price)}
              <span className="rp-card__unit"> / {formatUnitLabel(item.unit)}</span>
            </p>
            {delta ? (
              <span
                className={
                  (item.priceDeltaPct ?? 0) > 0 ? 'rp-delta rp-delta--up' : 'rp-delta rp-delta--down'
                }
              >
                {delta}
              </span>
            ) : null}
          </div>
        </button>
        <div className="rp-card__side">
          {qty === 0 ? (
            <button
              type="button"
              className="echat-btn echat-btn--primary rp-card__add"
              disabled={item.privateSupplierId == null}
              onClick={(event) => {
                event.stopPropagation()
                onAdd()
              }}
              aria-label="В корзину"
            >
              +
            </button>
          ) : (
            <div className="rp-stepper" role="group" aria-label="Количество">
              <button
                type="button"
                className="rp-stepper__btn"
                aria-label="Уменьшить"
                onClick={(event) => {
                  event.stopPropagation()
                  onQty(qty - 1)
                }}
              >
                −
              </button>
              <span className="rp-stepper__qty">{qty}</span>
              <button
                type="button"
                className="rp-stepper__btn"
                aria-label="Увеличить"
                onClick={(event) => {
                  event.stopPropagation()
                  onQty(qty + 1)
                }}
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rp-card__foot">
        {item.productTypeName ? <p className="rp-card__cat">{item.productTypeName}</p> : <span />}
        <span className="rp-card__chev" aria-hidden="true">
          ▾
        </span>
      </div>

      {open ? (
        <div className="rp-acc__body rp-card__body">
          <div className="rp-acc__row">
            <span>Поставщик</span>
            <span>{shortenSupplierName(supplier) || supplier}</span>
          </div>
          {item.previousPrice != null && item.price != null ? (
            <div className="rp-acc__row">
              <span>Было → стало</span>
              <span>
                {formatPrice(item.previousPrice)} → {formatPrice(item.price)}
              </span>
            </div>
          ) : null}
          <div className="rp-acc__row">
            <span>Дата обновления</span>
            <span>{formatUpdatedAt(item)}</span>
          </div>
          <div className="rp-acc__row">
            <span>Страна</span>
            <span>{item.country?.trim() || '—'}</span>
          </div>
          <div className="rp-acc__row">
            <span>Бренд</span>
            <span>{item.brand?.trim() || '—'}</span>
          </div>
          {item.packageSize?.trim() ? (
            <div className="rp-acc__row">
              <span>Фасовка</span>
              <span>{item.packageSize.trim()}</span>
            </div>
          ) : null}
          {item.externalSku?.trim() ? (
            <div className="rp-acc__row">
              <span>Артикул</span>
              <span>{item.externalSku.trim()}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

export const SearchScreen = ({ organizationId }: SearchScreenProps) => {
  const { lines: cartLines } = useCart()
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [supplierId, setSupplierId] = useState<number | null>(null)
  const [priceSort, setPriceSort] = useState<'asc' | 'desc' | null>(null)
  const [items, setItems] = useState<CatalogItemDto[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [suppliers, setSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<number | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [page, supplierList] = await Promise.all([
        listRestaurantMyPricesItems({
          organizationId,
          search: debounced || undefined,
          privateSupplierId: supplierId,
          size: 100,
        }),
        listRestaurantPrivateSuppliers(organizationId),
      ])
      setItems(page.items)
      setTotalElements(page.totalElements)
      setSuppliers(supplierList)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить прайсы')
    } finally {
      setLoading(false)
    }
  }, [debounced, organizationId, supplierId])

  useEffect(() => {
    void load()
  }, [load])

  const sortedItems = useMemo(() => {
    if (!priceSort) {
      return items
    }
    return [...items].sort((a, b) => {
      const pa = a.price ?? Number.POSITIVE_INFINITY
      const pb = b.price ?? Number.POSITIVE_INFINITY
      return priceSort === 'asc' ? pa - pb : pb - pa
    })
  }, [items, priceSort])

  const qtyOf = (catalogItemId: number) =>
    cartLines.find((line) => line.catalogItemId === catalogItemId)?.quantity ?? 0

  const handleAdd = (item: CatalogItemDto) => {
    if (item.privateSupplierId == null) {
      return
    }
    addToCart({
      catalogItemId: item.id,
      name: rawItemName(item),
      privateSupplierId: item.privateSupplierId,
      privateSupplierName: item.privateSupplierName ?? `Поставщик #${item.privateSupplierId}`,
      unit: formatUnitLabel(item.unit),
      unitPrice: item.price,
      note: null,
      brand: item.brand,
    })
  }

  const handleQty = (item: CatalogItemDto, next: number) => {
    if (next <= 0) {
      setCartQuantity(item.id, 0)
      return
    }
    if (qtyOf(item.id) === 0) {
      handleAdd(item)
      if (next > 1) {
        setCartQuantity(item.id, next)
      }
      return
    }
    setCartQuantity(item.id, next)
  }

  return (
    <section className="rp-screen" aria-label="Поиск по прайсам">
      <label className="rp-search">
        <span className="rp-search__icon" aria-hidden="true">
          ⌕
        </span>
        <span className="visually-hidden">Поиск</span>
        <input
          className="rp-search__input"
          type="search"
          placeholder="Найти позицию…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </label>

      <div className="rp-chips" role="group" aria-label="Поставщики">
        <button
          type="button"
          className={supplierId == null ? 'echat-chip echat-chip--selected' : 'echat-chip'}
          onClick={() => setSupplierId(null)}
        >
          Все прайсы
        </button>
        {suppliers.map((supplier) => (
          <button
            key={supplier.id}
            type="button"
            className={
              supplierId === supplier.id ? 'echat-chip echat-chip--selected' : 'echat-chip'
            }
            onClick={() => setSupplierId(supplier.id)}
          >
            {displaySupplierName(supplier.displayLabel, supplier.legalName)}
          </button>
        ))}
      </div>

      <div className="rp-toolbar rp-toolbar--split">
        <span className="rp-toolbar__count">
          {!loading && totalElements > 0
            ? `${totalElements.toLocaleString('ru-RU')} позиц. найдено`
            : ''}
        </span>
        <button
          type="button"
          className="echat-btn echat-btn--ghost rp-sort"
          onClick={() =>
            setPriceSort((current) => (current === 'asc' ? 'desc' : 'asc'))
          }
        >
          {priceSort === 'asc'
            ? 'По цене ↑'
            : priceSort === 'desc'
              ? 'По цене ↓'
              : 'По цене'}
        </button>
      </div>

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="echat-muted">Загрузка…</p> : null}

      {!loading && sortedItems.length === 0 ? (
        <div className="echat-inbox__empty">
          <p className="echat-inbox__empty-title">Ничего не найдено</p>
          <p className="echat-muted">
            Загрузите прайс на вкладке «Прайсы» или измените запрос.
          </p>
        </div>
      ) : null}

      <ul className="rp-list">
        {sortedItems.map((item) => {
          const qty = qtyOf(item.id)
          return (
            <SearchItemCard
              key={item.id}
              item={item}
              qty={qty}
              open={openId === item.id}
              onToggle={() => setOpenId((current) => (current === item.id ? null : item.id))}
              onAdd={() => handleAdd(item)}
              onQty={(next) => handleQty(item, next)}
            />
          )
        })}
      </ul>
    </section>
  )
}
